import React from "react";
import SubmenuHeader from "./SubmenuHeader";
import SubmenuRightSectionV2 from "./SubmenuRightSectionV2";
import InsightLeft from "./InsightLeft";
import SubmenuLinksLeft from "./SubmenuLinksLeft";
import ServiceLeft from "./ServiceLeft";
import Image from "next/image";

interface SubmenuPanelProps {
  item: any;
  index: number;
  motion: "open" | "swap";
  // Outgoing panel during a switch: kept rendered on top while it fades out.
  isLeaving: boolean;
  activeMain: any;
  activeSub: any;
  activeMenuLink: string | null;
  isWideScreen: boolean;
  baseUrl: string;
  onToggleMainMenu: (id: any) => void;
  onToggleSubMenu: (id: any) => void;
  onToggleMenu: (link?: string) => void;
  onSetActiveMenuLink: (label: string) => void;
  onSetActiveMenu: (label: string) => void;
}

// Fallbacks used only when a menu item has no `menu_image` set in the CMS, so
// a section never drops to a plain black promo card. Cycled by menu position
// to keep neighbouring sections visually distinct.
const PROMO_BG_FALLBACKS = [
  "/images/megamenu-right-bg-1.png",
  "/images/megamenu-right-bg-2.png",
  "/images/megamenu-right-bg-3.png",
  "/images/megamenu-right-bg-4.png",
  "/images/megamenu-right-bg-5.png",
];

// `menu_title` carries two different designs with one CMS field: a large stat
// ("9 in 10", "-28%", "4") and a headline ("What Are Agentic AI Solutions?").
// Short values get the stat treatment, longer ones the smaller headline.
const STAT_TITLE_MAX_LENGTH = 12;

const getMediaUrl = (media: any, baseUrl: string): string | undefined =>
  media?.data?.attributes?.url
    ? `${baseUrl}${media.data.attributes.url}`
    : undefined;

// CMS entries occasionally ship the raw Next.js route pattern (e.g.
// "/solutions/[slug]") instead of a real slug, which crashes client-side
// navigation. Treat those as unset rather than passing them to the router.
const isValidHref = (href?: string): href is string =>
  !!href && href !== "#!" && !/\[[^\]]+\]/.test(href);

const SubmenuPanelV2: React.FC<SubmenuPanelProps> = ({
  item,
  index,
  motion,
  isLeaving,
  activeMain,
  isWideScreen,
  baseUrl,
  onToggleMainMenu,
  onToggleMenu,
}) => {
  if (
    !item?.submenu ||
    item?.submenu.length === 0 ||
    (activeMain !== item.id && !isLeaving)
  ) {
    return null;
  }

  const handlePromoClick = (href?: string) => {
    onToggleMainMenu(null);
    if (isValidHref(href)) onToggleMenu(href);
  };

  // Any real link in the panel closes the menu. The route-change reset in the
  // parent can't catch links to the current page or hash-only changes
  // (e.g. /blog#category=whitepaper). New-tab clicks leave the menu open.
  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest("a");
    if (!anchor || !isValidHref(anchor.getAttribute("href") ?? undefined)) {
      return;
    }
    if (anchor.target === "_blank" || e.metaKey || e.ctrlKey || e.shiftKey) {
      return;
    }
    onToggleMenu();
  };

  // Ipad/mobile variants are optional in the CMS - when an editor hasn't
  // filled one in, the desktop image is reused for that breakpoint instead
  // of leaving it blank.
  const menuImageDesktopUrl =
    getMediaUrl(item?.menu_image, baseUrl) ??
    PROMO_BG_FALLBACKS[index % PROMO_BG_FALLBACKS.length];
  const menuImageIpadUrl =
    getMediaUrl(item?.menu_image_ipad, baseUrl) ?? menuImageDesktopUrl;
  const menuImageMobileUrl =
    getMediaUrl(item?.menu_image_mobile, baseUrl) ?? menuImageDesktopUrl;

  const bottomImageDesktopUrl = getMediaUrl(item?.bottom_image, baseUrl);
  const bottomImages = {
    desktop: bottomImageDesktopUrl,
    ipad:
      getMediaUrl(item?.bottom_image_ipad, baseUrl) ?? bottomImageDesktopUrl,
    mobile:
      getMediaUrl(item?.bottom_image_mobile, baseUrl) ?? bottomImageDesktopUrl,
  };

  // The CMS has no "layout" field, so the left-hand layout is derived from the
  // shape of the submenu an editor filled in. Every group of every submenu
  // entry is read, so adding a second group in the CMS is never silently
  // dropped.
  const submenu: any[] = item?.submenu ?? [];
  const groups: any[] = submenu.flatMap((sub: any) => sub?.right ?? []);
  const links: any[] = groups.flatMap((group: any) => group?.links ?? []);

  // Links carrying their own `cards` render as card columns (Insights).
  const hasCards = links.some((link: any) => link?.cards?.length);
  // Several submenu entries, each with its own heading, render as titled
  // columns with a flat link list underneath (Services).
  const hasTitledColumns =
    submenu.length > 1 && submenu.some((sub: any) => sub?.title);

  const title = (item?.menu_title ?? "").trim();
  const isStatTitle = title.length <= STAT_TITLE_MAX_LENGTH;
  // Only render the promo card when an editor has actually filled it in;
  // otherwise the link list takes the full panel width.
  const hasPromo = Boolean(
    title || item?.menu_subtitle || item?.menu_description,
  );

  return (
    <>
      <div
        data-lenis-prevent
        onClick={handlePanelClick}
        className={`${isLeaving ? "mega-panel-leave" : `mega-panel-${motion}`} border-t-1 border-t-blue-100 fixed left-0 right-0 xl:py-[48px] lg:py-[24px] lg:!pl-[0px] lg:!pr-[var(--menu-scrollbar-gap,0px)] py-[16px] w-full bg-white z-50 overflow-y-auto transition-[top,max-height] xl:!bg-size-[90%] lg:!bg-[url('/images/mega-menu-dots.png')] !bg-none !bg-size-[100%] `}
        style={{
          top: "calc(80px + var(--df-banner-offset, 0px))",
          height: isWideScreen
            ? "auto"
            : "auto",
          maxHeight: "calc(100vh - 140px - var(--df-banner-offset, 0px))",
          backgroundImage: "url('/images/mega-menu-dots.png')",
          backgroundSize: "90%",
          backgroundPosition: "bottom right",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="xxl:max-w-[1536px] xl:max-w-[1280px] lg:max-w-[1280px] container-header w-full mx-auto xl:px-[24px] lg:px-[55px] px-[24px]  bg-white relative z-[9] min-[1280px]:max-[1367px]:!p-[5px_60px] ">
          <div className="lg:hidden block absolute bottom-[-40px] right-0 w-full h-[300px] z-[-1]">
            <Image
              src={"/images/mega-menu-bg-mobile.png"}
              alt=""
              width={200}
              height={200}
              className="w-full h-full"
            />
          </div>
          <SubmenuHeader onBackClick={() => onToggleMainMenu(null)} />
          <div className={`mt-0 flex flex-col lg:flex-row h-full gap-0 `}>
            {/* Stacked up to xl so tablets get the full width for their link
                columns, with the promo card dropping below as a banner. */}
            <div className="flex flex-col xl:flex-row md:gap-[24px] gap-[15px] w-full">
              {hasCards ? (
                <InsightLeft links={links} bottomImages={bottomImages} />
              ) : hasTitledColumns ? (
                <ServiceLeft submenu={submenu} bottomImages={bottomImages} />
              ) : (
                <SubmenuLinksLeft right={groups} bottomImages={bottomImages} />
              )}

              {hasPromo && (
                <SubmenuRightSectionV2
                  subTitle={item?.menu_subtitle}
                  title={isStatTitle ? title : undefined}
                  titleSec={isStatTitle ? undefined : title}
                  bgImgDesktop={`url(${menuImageDesktopUrl})`}
                  bgImgIpad={`url(${menuImageIpadUrl})`}
                  bgImgMobile={`url(${menuImageMobileUrl})`}
                  description={item?.menu_description}
                  btnLabel={item?.menu_url_label}
                  btnHref={isValidHref(item?.menu_url) ? item.menu_url : "#!"}
                  onBtnClick={handlePromoClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmenuPanelV2;
